import React, { useState, useRef, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";

const API_BASE = "https://eluvent.com/api";

const SYLLABUS = {
  CBSE: {
    physics: ["light_reflection_refraction", "human_eye", "electricity", "magnetic_effects_of_current"],
    chemistry: ["chemical_reactions_equations", "acid_bases_salts", "metals_non_metals", "carbon_and_its_compounds"],
    biology: ["life_processes", "control_coordinations", "how_do_organisms_reproduce", "heredity"],
    mathematics: ["real_numbers", "polynomials", "linear_equations", "quadratic_equations", "arithmetic_progressions", "triangles", "coordinate_geometry", "trigonometry", "circles", "statistics", "probability"],
    english: ["reading_comprehension", "writing_skills", "grammar", "literature"],
    history: ["nationalism_in_europe", "nationalism_in_india", "making_of_a_global_world", "industrialisation"],
    geography: ["resources_and_development", "forest_and_wildlife", "water_resources", "agriculture", "minerals_and_energy"],
  },
  ICSE: {
    physics: ["force", "work_power_energy", "light", "sound", "electricity_magnetism", "modern_physics"],
    chemistry: ["periodic_properties", "chemical_bonding", "acid_bases_salt", "analytical_chemistry", "mole_concept_stoichiometry", "electrolysis", "metallurgy", "study_of_compounds", "organic_chemistry"],
    biology: ["basic_biology", "plant_physiology", "human_anatomy_and_physiology", "population", "human_evolution", "polution"],
    mathematics: ["rational_numbers", "exponents", "algebra", "geometry", "mensuration", "data_handling", "introduction_to_graphs"],
    english: ["composition", "letter_writing", "comprehension", "grammar"],
    history: ["indian_history", "world_history", "civics"],
    geography: ["map_study", "climate", "soil", "natural_vegetation", "water_resources"],
  },
};

const TestPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id") || "";

  const [board, setBoard] = useState("");
  const [subject, setSubject] = useState("");
  const [chapter, setChapter] = useState("");
  const [selectionDone, setSelectionDone] = useState(false);

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timer, setTimer] = useState(0);
  const timerRef = useRef(null);

  const funFacts = [
    "Did you know? The human eye can distinguish about 10 million colors! 🌈",
    "The Eiffel Tower grows 15 cm in summer due to thermal expansion! 🗼",
    "A single bolt of lightning contains enough energy to toast 100,000 slices of bread! ⚡",
    "Octopuses have three hearts and blue blood! 🐙",
    "There are more stars in the universe than grains of sand on Earth! ✨",
    "The first ever photograph was taken in 1826 by Joseph Nicéphore Niépce! 📸",
  ];
  const [funFact, setFunFact] = useState(funFacts[Math.floor(Math.random() * funFacts.length)]);

  useEffect(() => {
    if (loading) {
      const interval = setInterval(() => {
        setFunFact(funFacts[Math.floor(Math.random() * funFacts.length)]);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [loading]);

  const totalTime = 20 * 60; // 20 minutes

  const subjects = board ? Object.keys(SYLLABUS[board] || {}) : [];
  const chapters = subject ? SYLLABUS[board][subject] : [];

  const startTest = async () => {
    if (!subject || !chapter) {
      setError("Please select a subject and chapter.");
      return;
    }
    setSelectionDone(true);
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/test/generate`, { subject, chapter });
      setQuestions(res.data.questions);
      setTimer(totalTime);
      timerRef.current = setInterval(() => {
        setTimer(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleSubmit(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch {
      setError("Failed to generate questions. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (isAutoSubmit = false) => {
    if (submitted) return;
    clearInterval(timerRef.current);
    const answerList = questions.map((q, idx) => ({
      question_idx: idx,
      selected_option: answers[idx] || "",
      correct_option: q.correct
    }));
    const questionsList = questions.map(q => ({
      question: q.question,
      options: q.options,
      difficulty: q.difficulty || "medium"
    }));
    try {
      const res = await axios.post(`${API_BASE}/test/submit`, {
        session_id: sessionId,
        subject,
        chapter,
        answers: answerList,
        questions: questionsList,
      });
      setResult(res.data);
      setSubmitted(true);
      setTimeout(() => {
        window.location.href = `/exam-predictor?session_id=${sessionId}`;
      }, 2000);
    } catch {
      if (!isAutoSubmit) alert("Failed to submit test.");
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // ---- SELECTION SCREEN (Board, Subject, Chapter only) ----
  if (!selectionDone) {
    return (
      <div style={{ maxWidth: 500, margin: "60px auto", textAlign: "center", fontFamily: "Nunito, sans-serif", background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)", padding: 30, borderRadius: 20, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        <h2 style={{ color: "#1e293b" }}>📝 Topic Test</h2>
        <p style={{ color: "#475569" }}>Each test has 20 questions. Complete 5 tests to unlock your exam prediction!</p>

        <div style={{ marginBottom: 15 }}>
          <select value={board} onChange={(e) => { setBoard(e.target.value); setSubject(""); setChapter(""); }} style={{ padding: 8, borderRadius: 8, fontSize: 16 }}>
            <option value="">-- Select Board --</option>
            {Object.keys(SYLLABUS).map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>

        {board && (
          <div style={{ marginBottom: 15 }}>
            <select value={subject} onChange={(e) => { setSubject(e.target.value); setChapter(""); }} style={{ padding: 8, borderRadius: 8, fontSize: 16 }}>
              <option value="">-- Select Subject --</option>
              {subjects.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
          </div>
        )}

        {subject && (
          <div style={{ marginBottom: 15 }}>
            <select value={chapter} onChange={(e) => setChapter(e.target.value)} style={{ padding: 8, borderRadius: 8, fontSize: 16 }}>
              <option value="">-- Select Chapter --</option>
              {chapters.map(c => <option key={c} value={c}>{c.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</option>)}
            </select>
          </div>
        )}

        <button onClick={startTest} disabled={!chapter} style={{ padding: "12px 24px", background: chapter ? "#fca311" : "#ccc", color: "white", border: "none", borderRadius: 30, fontSize: 16, fontWeight: 700, cursor: chapter ? "pointer" : "not-allowed" }}>
          Start Test (20 Questions)
        </button>
        <p style={{ marginTop: 10, fontSize: 13, color: "#64748b" }}>
          ⏱️ 20 minutes for 20 questions
        </p>
        <Link to="/" style={{ display: "block", marginTop: 20, color: "#64748b" }}>← Back to Chat</Link>
      </div>
    );
  }

  // ---- LOADING SCREEN ----
  if (loading) return (
    <div style={{ textAlign: "center", padding: 40, fontFamily: "Nunito, sans-serif", background: "linear-gradient(135deg, #fef9c3 0%, #fde68a 100%)", minHeight: "100vh" }}>
      <div style={{ fontSize: 48, marginBottom: 8, animation: "bounce 1s infinite" }}>🤖</div>
      <p style={{ fontSize: 18, fontWeight: 600, color: "#1e293b" }}>⏳ Cooking up 20 fresh questions...</p>
      <p style={{ color: "#64748b", marginTop: 8, fontSize: 14 }}>
        Our AI is reading the textbook and double‑checking the answers.<br/>
        <span style={{ fontWeight: 600, color: "#fca311" }}>Usually takes 5‑10 seconds.</span>
      </p>
      <div style={{ width: 40, height: 40, border: "4px solid #e2e8f0", borderTop: "4px solid #fca311", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "20px auto" }}></div>
      <div style={{ marginTop: 30, background: "white", borderRadius: 12, padding: "10px 20px", display: "inline-block", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
        <span style={{ fontSize: 13, color: "#475569" }}>{funFact}</span>
      </div>
      <button onClick={() => { setLoading(false); setError("Test cancelled."); }} style={{ marginTop: 30, padding: "8px 20px", background: "transparent", color: "#94a3b8", border: "1px solid #cbd5e1", borderRadius: 30, fontSize: 12, cursor: "pointer" }}>
        Cancel
      </button>
    </div>
  );

  if (error) return <div style={{ textAlign: "center", padding: 40, color: "red" }}>{error}</div>;

  // ---- SHORT RESULT SCREEN (redirecting) ----
  if (submitted && result) {
    return (
      <div style={{ maxWidth: 700, margin: "40px auto", padding: 20, textAlign: "center", fontFamily: "Nunito, sans-serif" }}>
        <div style={{ fontSize: 48, marginBottom: 8 }}>🤖</div>
        <h1>📊 Test Submitted!</h1>
        <p style={{ fontSize: 18 }}>Your Score: {result.score}/{result.total} ({result.percentage}%)</p>
        <p style={{ marginTop: 20, color: "#64748b" }}>Redirecting to your updated Exam Predictor...</p>
        <div style={{ width: 40, height: 40, border: "4px solid #e2e8f0", borderTop: "4px solid #fca311", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "20px auto" }}></div>
      </div>
    );
  }

  // ---- QUIZ INTERFACE ----
  return (
    <div style={{ maxWidth: 700, margin: "40px auto", padding: 20, fontFamily: "Nunito, sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h1>📝 {subject} - {chapter.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</h1>
        <div style={{ background: timer < 60 ? "#fee2e2" : "#f0f9ff", padding: "10px 15px", borderRadius: 10, fontWeight: 700, fontSize: 18, color: timer < 60 ? "#dc2626" : "#1e40af" }}>
          ⏱️ {formatTime(timer)}
        </div>
      </div>
      {questions.map((q, idx) => (
        <div key={idx} style={{ background: "#f8fafc", padding: 15, borderRadius: 12, marginBottom: 15 }}>
          <p><strong>{idx+1}. {q.question}</strong></p>
          {Object.entries(q.options).map(([key, value]) => (
            <label key={key} style={{ display: "block", margin: 5, cursor: "pointer" }}>
              <input
                type="radio"
                name={`q${idx}`}
                value={key}
                checked={answers[idx] === key}
                onChange={() => setAnswers(prev => ({ ...prev, [idx]: key }))}
              /> {key.toUpperCase()}. {value}
            </label>
          ))}
        </div>
      ))}
      <button onClick={() => handleSubmit(false)} style={{ padding: "12px 24px", background: "#fca311", color: "white", border: "none", borderRadius: 30, fontSize: 16, fontWeight: 700, cursor: "pointer" }}>
        Submit Test
      </button>
    </div>
  );
};

export default TestPage;